import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Trash2, Edit2, X } from 'lucide-react';
import Modal from './Modal';
import { finishedGoodAPI } from '../../utils/api';
import { apiError } from '../../utils/helpers';

export const attrName = (a, lang) => (lang !== 'fa' && a['name_' + lang]) || a.name;

/** Display text of a product attribute value: option label (translated) or number + unit. */
export function attrValueLabel(a, v, lang) {
  if (v === undefined || v === null || v === '') return '';
  if (a.kind === 'select') {
    const o = (a.options || []).find(x => x.v === v);
    return o ? (o[lang] || o.fa || v) : String(v);
  }
  if (a.kind === 'number') return `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 3 })}${a.unit ? ' ' + a.unit : ''}`;
  return String(v);
}

/** The attribute inputs inside the product form. */
export function AttributeFields({ attrs, value, onChange }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const set = (code, v) => onChange({ ...(value || {}), [code]: v });
  const active = attrs.filter(a => a.is_active);
  if (!active.length) return null;
  return (
    <div className="attr-fields">
      {active.map(a => (
        <div key={a.id} className="form-group">
          <label className="form-label">{attrName(a, lang)}{a.unit ? ` (${a.unit})` : ''}</label>
          {a.kind === 'select'
            ? <select className="select" value={(value || {})[a.code] ?? ''} onChange={e => set(a.code, e.target.value)}>
                <option value="">—</option>{(a.options || []).map(o => <option key={o.v} value={o.v}>{o[lang] || o.fa}</option>)}
              </select>
            : <input className="input" type={a.kind === 'number' ? 'number' : 'text'} step="any" min="0" value={(value || {})[a.code] ?? ''} onChange={e => set(a.code, e.target.value)} />}
        </div>
      ))}
    </div>
  );
}

/** Manage the list of product attributes (coating, roll weight, thickness ... and any new one). */
export default function AttributeManager({ attrs, onClose, onChanged }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [edit, setEdit] = useState(null);
  const save = async () => {
    if (!edit.name?.trim()) return toast.error(t('fill_required'));
    try {
      if (edit.id) await finishedGoodAPI.updateAttribute(edit.id, edit);
      else await finishedGoodAPI.createAttribute(edit);
      toast.success(t('saved')); setEdit(null); onChanged();
    } catch (e) { toast.error(apiError(e, t)); }
  };
  const del = async a => {
    if (!window.confirm(t('attr_confirm_delete'))) return;
    try { await finishedGoodAPI.deleteAttribute(a.id); onChanged(); } catch (e) { toast.error(apiError(e, t)); }
  };
  const setOpt = (i, k, v) => setEdit(x => ({ ...x, options: x.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)) }));
  return (
    <>
    <Modal title={t('attr_manage')} size="modal-lg" onClose={onClose}>
      <p className="text-xs" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('attr_hint')}</p>
      <div className="attr-list">
        {attrs.map(a => (
          <div key={a.id} className={`attr-row ${a.is_active ? '' : 'off'}`}>
            <div><b>{attrName(a, lang)}</b><small>{t('attr_kind_' + a.kind)}{a.unit ? ` · ${a.unit}` : ''}</small></div>
            <div className="attr-opts">{a.kind === 'select' ? (a.options || []).map(o => <span key={o.v}>{o[lang] || o.fa}</span>) : null}</div>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEdit({ ...a, options: (a.options || []).map(o => ({ ...o })) })}><Edit2 size={13} /></button>
            <button className="btn btn-ghost btn-sm btn-icon" style={{ color: '#f87171' }} onClick={() => del(a)}><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
      <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => setEdit({ name: '', name_en: '', name_ru: '', kind: 'select', unit: '', options: [{ fa: '', en: '', ru: '' }] })}><Plus size={14} /> {t('attr_add')}</button>
    </Modal>

      {edit && (
        <Modal title={edit.id ? t('edit') : t('attr_add')} onClose={() => setEdit(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEdit(null)}>{t('cancel')}</button><button className="btn btn-primary" onClick={save}>{t('save')}</button></>}>
          <div className="form-group"><label className="form-label">{t('name')} (فارسی) *</label><input className="input" value={edit.name} onChange={e => setEdit(x => ({ ...x, name: e.target.value }))} placeholder={t('attr_name_ph')} /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">English</label><input className="input" dir="ltr" value={edit.name_en || ''} onChange={e => setEdit(x => ({ ...x, name_en: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Русский</label><input className="input" dir="ltr" value={edit.name_ru || ''} onChange={e => setEdit(x => ({ ...x, name_ru: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{t('attr_kind')}</label>
              <select className="select" value={edit.kind} disabled={!!edit.id} onChange={e => setEdit(x => ({ ...x, kind: e.target.value }))}>
                {['select', 'number', 'text'].map(k => <option key={k} value={k}>{t('attr_kind_' + k)}</option>)}</select></div>
            <div className="form-group"><label className="form-label">{t('unit')}</label><input className="input" value={edit.unit || ''} placeholder="kg, mm, m²" onChange={e => setEdit(x => ({ ...x, unit: e.target.value }))} /></div>
          </div>
          {edit.kind === 'select' && <div className="form-group">
            <label className="form-label">{t('attr_options')}</label>
            {edit.options.map((o, i) => (
              <div key={i} className="attr-opt-row">
                <input className="input" placeholder="فارسی" value={o.fa || ''} onChange={e => setOpt(i, 'fa', e.target.value)} />
                <input className="input" dir="ltr" placeholder="English" value={o.en || ''} onChange={e => setOpt(i, 'en', e.target.value)} />
                <input className="input" dir="ltr" placeholder="Русский" value={o.ru || ''} onChange={e => setOpt(i, 'ru', e.target.value)} />
                <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setEdit(x => ({ ...x, options: x.options.filter((_, j) => j !== i) }))}><X size={13} /></button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => setEdit(x => ({ ...x, options: [...x.options, { fa: '', en: '', ru: '' }] }))}><Plus size={13} /> {t('attr_add_option')}</button>
          </div>}
        </Modal>
      )}
    </>
  );
}
